import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Marquee from "react-fast-marquee";
import { ArrowRight, Flame, Zap, Users, Instagram, Facebook, Clock, Lock, Building2, Send, Truck, Wine, ShoppingBag } from "lucide-react";
import { fetchProducts, fetchTeaser, subscribeNewsletter } from "../lib/api";
import { toast } from "sonner";
import { useCart } from "../context/CartContext";
import { useConfig } from "../context/ConfigContext";
import RequestModal from "../components/RequestModal";
import SEO from "../components/SEO";
import { ProefboxBlock } from "../components/ProefboxBlock";

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/91b039dc-51e4-4e53-aaae-68155d45e4bb/images/a0067dc0bd562b6806aa88afb54de5c878fdc2029fb5757d6f6fb7a1449dd1d7.jpeg";
const STORY_IMG =
  "https://static.prod-images.emergentagent.com/jobs/91b039dc-51e4-4e53-aaae-68155d45e4bb/images/7facf976f9eb478191648aa52cea98a77e16f5b2845bf906437e930acc485eee.png";

const SHOT_WORDS_NL = { 1: "ÉÉN SHOT", 2: "TWEE SHOTS", 3: "DRIE SHOTS", 4: "VIER SHOTS", 5: "VIJF SHOTS" };

const Home = () => {
  const [products, setProducts] = useState([]);
  const [teaser, setTeaser] = useState(null);
  const shots = products.filter((p) => (p.product_type || "shot") === "shot");
  const proefbox = products.find((p) => p.id === "proefbox") || products.find((p) => p.product_type === "box");
  const product = shots[0] || null;
  const { addItem } = useCart();
  const { ecommerceEnabled } = useConfig();
  const [reqOpen, setReqOpen] = useState(false);
  const [reqProduct, setReqProduct] = useState(null);
  const [reqVariant, setReqVariant] = useState(null);

  const openRequest = (p, v) => {
    setReqProduct(p);
    setReqVariant(v || null);
    setReqOpen(true);
  };

  useEffect(() => {
    fetchProducts().then((ps) => setProducts(ps || [])).catch(() => {});
    fetchTeaser().then((t) => setTeaser(t)).catch(() => {});
  }, []);

  // Visible shot count for dynamic copy
  const visibleCount = shots.length;
  const totalCount = visibleCount + (teaser ? 1 : 0);
  const shotsWord = SHOT_WORDS_NL[totalCount] || `${totalCount} SHOTS`;
  const nextShotNum = String(visibleCount + (teaser ? 2 : 1)).padStart(3, "0");


  return (
    <div data-testid="home-page" className="relative">
      <SEO
        title="Nederlandse shots — bedacht op een familieborrel"
        description="Tik Hem Aan! is een Nederlandse line-up van 20ml shots — klein van formaat, groot van karakter. Voor mooie momenten, wilde nachten, en alles ertussenin. Nu ook zakelijk verkrijgbaar."
        path="/"
      />
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0"
          data-testid="hero-bg-image"
          style={{
            backgroundImage: `url(${HERO_IMG})`,
            backgroundSize: "cover",
            backgroundPosition: "70% center",
          }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(90deg, rgba(5,5,5,0.92) 0%, rgba(5,5,5,0.7) 40%, rgba(5,5,5,0.15) 75%, rgba(5,5,5,0.25) 100%)",
          }}
        />
        <div
          className="absolute inset-0"
          style={{ background: "linear-gradient(180deg, transparent 65%, rgba(5,5,5,1) 100%)" }}
        />
        <div className="absolute inset-0 bg-black/50 md:hidden" />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8 pt-28 pb-40 sm:pt-36 sm:pb-56">
          <div className="flex items-center gap-3 rise">
            <span className="h-px w-12 bg-[var(--tha-red)]" />
            <span className="text-xs font-mono-x tracking-[0.3em] text-[var(--tha-red)]">
              GEBOREN BIJ EEN FAMILIEBORREL
            </span>
          </div>

          <h1
            data-testid="hero-title"
            className="font-display text-[18vw] sm:text-[14vw] md:text-[11rem] mt-6 leading-[0.85] text-white rise-2"
          >
            TIK HEM
            <br />
            <span className="neon-red-text">AAN!</span>
          </h1>

          <p
            data-testid="hero-tagline"
            className="mt-8 text-[var(--tha-muted)] max-w-xl text-base sm:text-lg leading-relaxed rise-3"
          >
            Eén kleine fles. Eén grote tik. Tik Hem Aan! is een line-up van 20ml shotjes —
            klein van formaat, groot van karakter. Voor mooie momenten, wildste nachten,
            en alles ertussenin.
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-3 rise-4">
            {ecommerceEnabled ? (
              product?.coming_soon ? (
                <Link
                  data-testid="hero-coming-soon-cta"
                  to="/shop"
                  className="btn-red px-7 py-4 font-display tracking-wider text-base inline-flex items-center gap-2"
                >
                  ONTDEK DE LINE-UP <ArrowRight size={16} />
                </Link>
              ) : (
                <>
                  <Link
                    data-testid="hero-shop-cta"
                    to="/shop"
                    className="btn-red px-7 py-4 font-display tracking-wider text-base inline-flex items-center gap-2"
                  >
                    <ShoppingBag size={16} /> BESTEL NU <ArrowRight size={16} />
                  </Link>
                  <Link
                    data-testid="hero-b2b-secondary-cta"
                    to="/zakelijk"
                    className="btn-ghost-red px-7 py-4 font-display tracking-wider text-base inline-flex items-center gap-2"
                  >
                    <Building2 size={16} /> ZAKELIJK
                  </Link>
                </>
              )
            ) : (
              <>
                <Link
                  data-testid="hero-b2b-cta"
                  to="/zakelijk"
                  className="btn-red px-7 py-4 font-display tracking-wider text-base inline-flex items-center gap-2"
                >
                  <Building2 size={16} /> ZAKELIJK AANVRAGEN
                </Link>
                <Link
                  data-testid="hero-consumer-cta"
                  to="/shop"
                  className="btn-ghost-red px-7 py-4 font-display tracking-wider text-base inline-flex items-center gap-2"
                >
                  <Send size={16} /> PARTICULIER AANVRAGEN
                </Link>
              </>
            )}
            <Link
              to="/verhaal"
              className="btn-ghost-red px-7 py-4 font-display tracking-wider text-base"
            >
              ONS VERHAAL
            </Link>
          </div>
          {product?.coming_soon && (
            <div className="mt-5 inline-flex items-center gap-2 text-xs font-mono-x tracking-[0.3em] text-[var(--tha-red)]">
              <span className="w-2 h-2 bg-[var(--tha-red)] rounded-full animate-pulse" />
              BINNENKORT VERKRIJGBAAR
            </div>
          )}
        </div>
      </section>

      {/* MARQUEE */}
      <section className="relative py-6 border-y border-[var(--tha-border)] bg-[#070707]">
        <Marquee gradient={false} speed={60} className="overflow-hidden">
          {Array.from({ length: 6 }).map((_, idx) => (
            <span key={idx} className="flex items-center gap-8 px-8">
              <span className="marquee-text-solid">TIK HEM AAN!</span>
              <Flame size={28} className="text-[var(--tha-red)]" />
              <span className="marquee-text">PROOST</span>
              <Flame size={28} className="text-[var(--tha-red)]" />
              <span className="marquee-text-solid">ÉÉN TIK &amp; GO</span>
              <Flame size={28} className="text-[var(--tha-red)]" />
              <span className="marquee-text">20ML / 10–14,5%</span>
              <Flame size={28} className="text-[var(--tha-red)]" />
            </span>
          ))}
        </Marquee>
      </section>

      {/* FEATURED PRODUCTS — TWO TILES */}
      <section className="relative py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="flex items-center gap-3">
            <span className="h-px w-12 bg-[var(--tha-red)]" />
            <span className="text-xs font-mono-x tracking-[0.3em] text-[var(--tha-red)]">
              DE LINE-UP
            </span>
          </div>
          <h2 className="font-display text-5xl sm:text-7xl mt-6 max-w-3xl leading-none">
            {shotsWord}. <span className="neon-red-text">ÉÉN FAMILIE.</span>
          </h2>
          <p className="mt-5 text-[var(--tha-muted)] max-w-xl">
            Begonnen bij een borrel, uitgewerkt tot een lijn van shots met allemaal hun
            eigen tik. En er komt nog meer aan.
          </p>

          <div className={`mt-14 grid grid-cols-1 md:grid-cols-2 ${visibleCount >= 3 ? "lg:grid-cols-3" : ""} gap-px bg-[var(--tha-border)]`}>
            {shots.slice(0, 3).map((p, idx) => {
              const accent = p.accent_color || "#ff1e1e";
              const variant = (p.variants || []).find((v) => v.popular) || p.variants?.[0];
              const num = String(idx + 1).padStart(3, "0");
              const available = typeof p.available === "number" ? p.available : p.stock;
              const tileStockLow = !p.coming_soon && available > 0 && available < 25;
              return (
                <div
                  key={p.id}
                  data-testid={`home-product-tile-${p.id}`}
                  className="relative bg-[#070707] overflow-hidden group"
                >
                  <div
                    className="relative aspect-[4/5] overflow-hidden border-b border-[var(--tha-border)]"
                    style={{
                      background: `radial-gradient(circle at center, ${accent}22 0%, transparent 60%)`,
                    }}
                  >
                    <img
                      src={p.image}
                      alt={p.name}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute top-4 left-4 text-[10px] font-mono-x tracking-widest text-white/80">
                      #{num} {p.name.toUpperCase()}
                    </div>
                    {tileStockLow && (
                      <div
                        data-testid={`home-stock-badge-${p.id}`}
                        className="absolute top-4 right-4 inline-flex items-center gap-1.5 px-2.5 py-1 border text-[9px] font-mono-x tracking-[0.25em] animate-pulse"
                        style={{
                          borderColor: accent,
                          color: accent,
                          background: "rgba(5,5,5,0.85)",
                          boxShadow: `0 0 14px ${accent}66`,
                        }}
                      >
                        <Flame size={10} /> NOG {available}
                      </div>
                    )}
                    {p.coming_soon && (
                      <div
                        className="absolute inset-0 flex items-center justify-center"
                        style={{
                          background:
                            "radial-gradient(ellipse at center, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.25) 60%, rgba(0,0,0,0.15) 100%)",
                        }}
                      >
                        <div className="text-center px-6">
                          <div
                            className="inline-flex items-center gap-2 text-[10px] font-mono-x tracking-[0.3em]"
                            style={{ color: accent, textShadow: "0 2px 12px rgba(0,0,0,0.9)" }}
                          >
                            <Clock size={12} /> BINNENKORT
                          </div>
                          <div
                            className="font-display text-4xl sm:text-5xl text-white mt-3 leading-none"
                            style={{
                              textShadow:
                                "0 4px 24px rgba(0,0,0,0.95), 0 2px 8px rgba(0,0,0,0.9)",
                            }}
                          >
                            COMING <span style={{ color: accent }}>SOON</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="p-7 sm:p-9">
                    <div
                      className="text-[10px] font-mono-x tracking-[0.3em]"
                      style={{ color: accent }}
                    >
                      SHOT #{num}
                    </div>
                    <h3 className="font-display text-4xl sm:text-5xl text-white mt-3 leading-none">
                      {p.name.toUpperCase()}
                    </h3>
                    <p className="mt-3 text-sm text-[var(--tha-muted)] italic">
                      {p.tagline}
                    </p>

                    <div className="mt-6 flex items-center gap-3 text-[10px] font-mono-x tracking-widest text-[var(--tha-muted)]">
                      <span>{p.alcohol}</span>
                      <span className="w-1 h-1 bg-[var(--tha-muted)] rounded-full" />
                      <span>{p.volume.toUpperCase()}</span>
                      {ecommerceEnabled && variant && (
                        <>
                          <span className="w-1 h-1 bg-[var(--tha-muted)] rounded-full" />
                          <span>vanaf € {p.variants[0]?.price?.toFixed(2)}</span>
                        </>
                      )}
                    </div>

                    <div className="mt-7 flex flex-wrap gap-2">
                      {p.coming_soon ? (
                        <Link
                          to="/shop"
                          data-testid={`home-tile-cta-${p.id}`}
                          className="px-5 py-3 font-display tracking-wider text-sm inline-flex items-center gap-2 transition-colors"
                          style={{ background: accent, color: "#050505" }}
                        >
                          MELD JE AAN <ArrowRight size={14} />
                        </Link>
                      ) : ecommerceEnabled ? (
                        <button
                          data-testid={`home-tile-add-${p.id}`}
                          onClick={() => {
                            if (!variant) return;
                            addItem({
                              id: variant.id,
                              name: `${p.name} — ${variant.label}`,
                              price: variant.price,
                              units: variant.units,
                              image: p.image,
                            }, 1);
                          }}
                          className="px-5 py-3 font-display tracking-wider text-sm inline-flex items-center gap-2 transition-colors"
                          style={{ background: accent, color: "#050505" }}
                        >
                          IN MANDJE
                        </button>
                      ) : (
                        <button
                          data-testid={`home-tile-request-${p.id}`}
                          onClick={() => openRequest(p, variant)}
                          className="px-5 py-3 font-display tracking-wider text-sm inline-flex items-center gap-2 transition-colors"
                          style={{ background: accent, color: "#050505" }}
                        >
                          <Send size={14} /> AANVRAGEN
                        </button>
                      )}
                      <Link
                        to="/shop"
                        className="btn-ghost-red px-5 py-3 font-display tracking-wider text-sm"
                      >
                        BEKIJKEN
                      </Link>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {teaser ? (
            <TeaserBanner teaser={teaser} />
          ) : (
            <div className="mt-10 text-center">
              <div className="inline-flex items-center gap-3 text-xs font-mono-x tracking-[0.3em] text-[var(--tha-muted)]">
                <span className="h-px w-8 bg-[var(--tha-border)]" />
                EN SHOT #{nextShotNum} IS ONDERWEG
                <span className="h-px w-8 bg-[var(--tha-border)]" />
              </div>
            </div>
          )}
        </div>
      </section>

      {proefbox && <ProefboxBlock box={proefbox} shots={shots} onRequest={openRequest} />}

      {/* ── B2B HERO — prominent zakelijk section ─────────────────────── */}
      <B2BHeroSection />

      {/* WAAROM */}
      <section className="relative py-24 border-t border-[var(--tha-border)]">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="flex items-center gap-3">
            <span className="h-px w-12 bg-[var(--tha-red)]" />
            <span className="text-xs font-mono-x tracking-[0.3em] text-[var(--tha-red)]">
              WAAROM TIK HEM AAN!
            </span>
          </div>
          <h2 className="font-display text-5xl sm:text-7xl mt-6 max-w-3xl">
            KLEIN. <span className="neon-red-text">SCHERP.</span> ONVERGETELIJK.
          </h2>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-px bg-[var(--tha-border)]">
            {[
              {
                icon: Flame,
                title: "EIGEN KARAKTER",
                desc: "Geen one-size-fits-all. Elke smaak heeft zijn eigen kleur, karakter en bijbehorende avond.",
              },
              {
                icon: Zap,
                title: "ÉÉN TIK & GO",
                desc: "20ml van puur karakter. Klein genoeg om door te geven, groot genoeg om te onthouden.",
              },
              {
                icon: Users,
                title: "JAN & BERT",
                desc: "Bedacht op een familieborrel door twee vrienden. Geen pitchdeck, geen agency — gewoon lef.",
              },
            ].map((f, i) => (
              <div key={i} className="bg-[#070707] p-8">
                <f.icon size={28} className="text-[var(--tha-red)]" />
                <div className="font-display text-2xl mt-5 tracking-wide">
                  {f.title}
                </div>
                <p className="mt-3 text-sm text-[var(--tha-muted)] leading-relaxed">
                  {f.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* STORY TEASER */}
      <section className="relative py-24 border-t border-[var(--tha-border)]">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 items-stretch">
          <div className="lg:col-span-6 relative aspect-[4/3] border border-[var(--tha-border)] overflow-hidden">
            <img
              src={STORY_IMG}
              alt="Familieavond"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black/30" />
          </div>
          <div className="lg:col-span-6 flex flex-col justify-center">
            <div className="text-xs font-mono-x tracking-[0.3em] text-[var(--tha-red)]">
              HET VERHAAL
            </div>
            <h2 className="font-display text-5xl sm:text-6xl mt-5 leading-none">
              JAN & BERT.
              <br />
              EEN BORREL.
              <br />
              <span className="neon-red-text">ÉÉN IDEE.</span>
            </h2>
            <p className="mt-6 text-[var(--tha-muted)] max-w-lg leading-relaxed">
              Het begon op een zaterdagavond bij Jans ouders thuis. Twee vrienden die
              altijd al iets samen wilden bouwen, een familieborrel die te lekker liep,
              en één gesprek dat alles in gang zette: de markt had behoefte aan iets
              nieuws — en wij gingen het maken.
            </p>
            <div className="mt-8">
              <Link
                to="/verhaal"
                className="btn-ghost-red px-6 py-3 font-display tracking-wider inline-flex items-center gap-2"
              >
                LEES VERDER <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* SOCIAL — VOLG ONS */}
      <section
        data-testid="social-section"
        className="relative py-24 border-t border-[var(--tha-border)]"
      >
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="flex items-center gap-3">
            <span className="h-px w-12 bg-[var(--tha-red)]" />
            <span className="text-xs font-mono-x tracking-[0.3em] text-[var(--tha-red)]">
              VOLG ONS
            </span>
          </div>
          <h2 className="font-display text-5xl sm:text-7xl mt-6 max-w-3xl leading-none">
            STAY IN <span className="neon-red-text">THE LOOP.</span>
          </h2>
          <p className="mt-5 text-[var(--tha-muted)] max-w-xl">
            Backstage van familieavonden, dronken adviezen en als eerste op de hoogte
            van drops. Volg ons en mis niks.
          </p>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-px bg-[var(--tha-border)]">
            <a
              data-testid="social-tiktok-tile"
              href="https://www.tiktok.com/@tikhemaanshot"
              target="_blank"
              rel="noreferrer"
              className="group relative overflow-hidden bg-[#070707] p-8 sm:p-10 transition-colors hover:bg-[#0f0f0f]"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="w-14 h-14 border border-[var(--tha-border)] flex items-center justify-center group-hover:border-[var(--tha-red)] transition-colors">
                  <TikTokIcon size={26} />
                </div>
                <ArrowRight
                  size={20}
                  className="text-[var(--tha-muted)] group-hover:text-[var(--tha-red)] group-hover:translate-x-1 transition-all"
                />
              </div>
              <div className="mt-8 font-display text-4xl sm:text-5xl text-white leading-none">
                TIKTOK
              </div>
              <div className="mt-3 text-sm font-mono-x tracking-widest text-[var(--tha-muted)] group-hover:text-[var(--tha-red)] transition-colors">
                @TIKHEMAANSHOT
              </div>
              <div className="mt-6 text-xs text-[var(--tha-muted)] leading-relaxed max-w-sm">
                Korte clips, dronken proefsessies en familie-content. Volg ons en mis
                geen tik.
              </div>
            </a>

            <a
              data-testid="social-instagram-tile"
              href="https://www.instagram.com/tikhemaannl"
              target="_blank"
              rel="noreferrer"
              className="group relative overflow-hidden bg-[#070707] p-8 sm:p-10 transition-colors hover:bg-[#0f0f0f]"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="w-14 h-14 border border-[var(--tha-border)] flex items-center justify-center group-hover:border-[var(--tha-red)] transition-colors">
                  <Instagram size={26} />
                </div>
                <ArrowRight
                  size={20}
                  className="text-[var(--tha-muted)] group-hover:text-[var(--tha-red)] group-hover:translate-x-1 transition-all"
                />
              </div>
              <div className="mt-8 font-display text-4xl sm:text-5xl text-white leading-none">
                INSTAGRAM
              </div>
              <div className="mt-3 text-sm font-mono-x tracking-widest text-[var(--tha-muted)] group-hover:text-[var(--tha-red)] transition-colors">
                @TIKHEMAANNL
              </div>
              <div className="mt-6 text-xs text-[var(--tha-muted)] leading-relaxed max-w-sm">
                Foto's van drops, events en alles wat er buiten kantooruren gebeurt.
              </div>
            </a>

            <a
              data-testid="social-facebook-tile"
              href="https://www.facebook.com/profile.php?id=61589678580761"
              target="_blank"
              rel="noreferrer"
              className="group relative overflow-hidden bg-[#070707] p-8 sm:p-10 transition-colors hover:bg-[#0f0f0f]"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="w-14 h-14 border border-[var(--tha-border)] flex items-center justify-center group-hover:border-[var(--tha-red)] transition-colors">
                  <Facebook size={26} />
                </div>
                <ArrowRight
                  size={20}
                  className="text-[var(--tha-muted)] group-hover:text-[var(--tha-red)] group-hover:translate-x-1 transition-all"
                />
              </div>
              <div className="mt-8 font-display text-4xl sm:text-5xl text-white leading-none">
                FACEBOOK
              </div>
              <div className="mt-3 text-sm font-mono-x tracking-widest text-[var(--tha-muted)] group-hover:text-[var(--tha-red)] transition-colors">
                TIK HEM AAN!
              </div>
              <div className="mt-6 text-xs text-[var(--tha-muted)] leading-relaxed max-w-sm">
                Updates, evenementen en de officiële pagina. Like ons en blijf op de
                hoogte.
              </div>
            </a>
          </div>
        </div>
      </section>

      <RequestModal
        open={reqOpen}
        onClose={() => setReqOpen(false)}
        product={reqProduct}
        variant={reqVariant}
      />
    </div>
  );
};

const B2B_BG_IMG =
  "https://static.prod-images.emergentagent.com/jobs/91b039dc-51e4-4e53-aaae-68155d45e4bb/images/7facf976f9eb478191648aa52cea98a77e16f5b2845bf906437e930acc485eee.png";

const B2BHeroSection = () => (
  <section
    data-testid="home-b2b-section"
    className="relative overflow-hidden border-t border-[var(--tha-border)]"
  >
    {/* Grid pattern base */}
    <div
      className="absolute inset-0"
      style={{
        backgroundImage:
          "linear-gradient(rgba(255,30,30,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(255,30,30,0.06) 1px, transparent 1px)",
        backgroundSize: "80px 80px",
      }}
    />
    {/* Brand photo — desaturated + red-tinted */}
    <div
      className="absolute inset-0 opacity-40"
      style={{
        backgroundImage: `url(${B2B_BG_IMG})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        filter: "grayscale(0.6) contrast(1.1)",
      }}
    />
    {/* Deep gradient */}
    <div
      className="absolute inset-0"
      style={{
        background:
          "linear-gradient(115deg, rgba(5,5,5,0.98) 0%, rgba(5,5,5,0.82) 35%, rgba(30,0,0,0.75) 65%, rgba(255,30,30,0.35) 100%)",
      }}
    />
    {/* Red glow accent */}
    <div
      className="absolute right-0 top-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full opacity-40"
      style={{
        background: "radial-gradient(circle, rgba(255,30,30,0.35) 0%, transparent 70%)",
        filter: "blur(60px)",
      }}
    />
    <div className="relative mx-auto max-w-7xl px-5 sm:px-8 py-28 sm:py-36">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7">
          <div className="flex items-center gap-3">
            <span className="h-px w-12 bg-[var(--tha-red)]" />
            <span className="text-xs font-mono-x tracking-[0.3em] text-[var(--tha-red)]">
              VOOR HORECA · EVENTS · GROOTHANDEL
            </span>
          </div>
          <h2 className="font-display text-6xl sm:text-7xl lg:text-[7rem] mt-6 leading-[0.85]">
            SHOTS VOOR
            <br />
            <span className="neon-red-text">JOUW ZAAK.</span>
          </h2>
          <p className="mt-8 text-white/80 max-w-xl text-base sm:text-lg leading-relaxed">
            Klein formaat, hoge omloop. Onze shots zijn geboren aan de bar en
            gemaakt om door te tikken. Vraag een offerte op maat aan — we tikken
            je binnen 1 werkdag terug.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-3">
            <Link
              to="/zakelijk"
              data-testid="home-b2b-cta"
              className="btn-red px-8 py-4 font-display tracking-wider text-base inline-flex items-center gap-2"
            >
              <Building2 size={16} /> VRAAG OFFERTE AAN <ArrowRight size={16} />
            </Link>
            <a
              href="mailto:tikhemaannl@gmail.com"
              className="btn-ghost-red px-6 py-4 font-display tracking-wider text-base inline-flex items-center gap-2"
            >
              MAIL DIRECT
            </a>
          </div>
        </div>
        <div className="lg:col-span-5">
          <div className="grid grid-cols-1 gap-px bg-[var(--tha-border)]">
            {[
              { icon: Wine, title: "3 SMAKEN + BOXEN", desc: "Red Rush · Electric Energy · Dark Drop" },
              { icon: Truck, title: "SNELLE LEVERING", desc: "Vanuit Assen · 3-5 werkdagen · Op afspraak sneller" },
              { icon: Building2, title: "OP MAAT VOORSTEL", desc: "Prijs op basis van volume · Geen verplichting" },
            ].map((f, i) => (
              <div key={i} className="bg-[#0a0a0a]/90 p-6 backdrop-blur-sm">
                <div className="flex items-start gap-4">
                  <f.icon size={22} className="text-[var(--tha-red)] mt-1 shrink-0" />
                  <div>
                    <div className="font-display text-xl tracking-wide">{f.title}</div>
                    <div className="mt-1 text-xs text-white/60 font-mono-x">{f.desc}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);

const TeaserBanner = ({ teaser }) => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const accent = teaser.accent_color || "#d4a04a";

  // Live ticker for countdown
  useEffect(() => {
    if (!teaser.reveal_at) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [teaser.reveal_at]);

  // When countdown hits zero, refresh the page so the auto-reveal kicks in.
  useEffect(() => {
    if (!teaser.reveal_at) return undefined;
    const target = new Date(teaser.reveal_at).getTime();
    if (now >= target) {
      const t = setTimeout(() => window.location.reload(), 3000);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [now, teaser.reveal_at]);

  const target = teaser.reveal_at ? new Date(teaser.reveal_at).getTime() : null;
  const diff = target ? Math.max(0, target - now) : null;
  const days = diff != null ? Math.floor(diff / 86400000) : null;
  const hours = diff != null ? Math.floor((diff % 86400000) / 3600000) : null;
  const minutes = diff != null ? Math.floor((diff % 3600000) / 60000) : null;
  const seconds = diff != null ? Math.floor((diff % 60000) / 1000) : null;

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const r = await subscribeNewsletter(email);
      toast.success(r.message || "Aangemeld — wij houden je op de hoogte!");
      setEmail("");
    } catch {
      toast.error("Aanmelden mislukt. Probeer opnieuw.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      id="teaser"
      data-testid="teaser-banner"
      className="mt-16 relative overflow-hidden border border-[var(--tha-border)] scroll-mt-24"
      style={{
        background: `
          radial-gradient(ellipse at 25% 50%, ${accent}33 0%, transparent 55%),
          radial-gradient(ellipse at 80% 30%, ${accent}1a 0%, transparent 50%),
          #050505
        `,
      }}
    >
      <div
        className="absolute top-0 left-0 right-0 h-px"
        style={{
          background: `linear-gradient(90deg, transparent 0%, ${accent} 50%, transparent 100%)`,
        }}
      />

      <div className="grid grid-cols-1 md:grid-cols-12 gap-0">
        <div className="md:col-span-5 relative aspect-square md:aspect-auto md:min-h-[460px] overflow-hidden">
          <img
            src={teaser.image}
            alt={teaser.name}
            className="absolute inset-0 w-full h-full object-cover"
            style={{ filter: "brightness(0.55) saturate(1.15)" }}
          />
          <div
            className="absolute inset-0"
            style={{
              background: `
                linear-gradient(90deg, transparent 0%, #050505 95%),
                radial-gradient(circle at 50% 40%, transparent 0%, rgba(5,5,5,0.5) 80%)
              `,
            }}
          />
          <div
            className="absolute top-6 left-6 inline-flex items-center gap-2 px-3 py-2 border text-[10px] font-mono-x tracking-[0.3em]"
            style={{ borderColor: accent, color: accent, background: "rgba(5,5,5,0.6)" }}
          >
            <Lock size={10} /> GEHEIME DROP
          </div>
        </div>

        <div className="md:col-span-7 p-8 sm:p-12 md:p-14 flex flex-col justify-center">
          <div
            className="inline-flex items-center gap-2 text-[10px] font-mono-x tracking-[0.3em]"
            style={{ color: accent }}
          >
            <Clock size={12} /> BINNENKORT — DERDE SMAAK
          </div>
          <h3
            className="font-display text-5xl sm:text-7xl mt-5 leading-none text-white"
            style={{ textShadow: `0 0 40px ${accent}55` }}
          >
            MEET <span style={{ color: accent }}>{teaser.name.toUpperCase()}</span>
          </h3>
          {teaser.tagline && (
            <p
              className="mt-5 text-base sm:text-lg max-w-md italic"
              style={{ color: "#cfcfcf" }}
            >
              {teaser.tagline}
            </p>
          )}

          {/* Countdown */}
          {target && (
            <div
              data-testid="teaser-countdown"
              className="mt-8 inline-flex items-center gap-3 sm:gap-4"
            >
              {diff === 0 ? (
                <div
                  className="font-display text-3xl sm:text-4xl"
                  style={{ color: accent }}
                >
                  HET IS ZOVER — TIK HEM AAN
                </div>
              ) : (
                <>
                  <CountdownCell value={days} label="DAG" accent={accent} />
                  <CountdownCell value={hours} label="UUR" accent={accent} pad />
                  <CountdownCell value={minutes} label="MIN" accent={accent} pad />
                  <CountdownCell value={seconds} label="SEC" accent={accent} pad />
                </>
              )}
            </div>
          )}

          <p className="mt-6 text-sm text-[var(--tha-muted)] max-w-md leading-relaxed">
            Schrijf je in en wees er als eerste bij. Op de release-dag krijg je
            automatisch een mailtje zodra hij aan tikt.
          </p>

          <form
            data-testid="teaser-newsletter-form"
            onSubmit={submit}
            className="mt-6 flex flex-col sm:flex-row gap-2 max-w-md"
          >
            <input
              data-testid="teaser-newsletter-email"
              type="email"
              required
              placeholder="jouw@email.nl"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="flex-1 px-4 py-3 text-sm bg-[#0a0a0a] border border-[var(--tha-border)] focus:outline-none"
            />
            <button
              data-testid="teaser-newsletter-submit"
              disabled={loading}
              type="submit"
              className="px-6 py-3 font-display tracking-wider text-sm inline-flex items-center justify-center gap-2 transition-transform hover:scale-[1.02] disabled:opacity-60"
              style={{ background: accent, color: "#050505" }}
            >
              {loading ? "..." : "MELD MIJ AAN"}
            </button>
          </form>

          <div className="mt-6 text-[10px] font-mono-x tracking-[0.3em] text-[var(--tha-dim)]">
            NIX18 — GENIET, MAAR DRINK MET MATE
          </div>
        </div>
      </div>
    </div>
  );
};

const CountdownCell = ({ value, label, accent, pad = false }) => (
  <div className="flex flex-col items-center">
    <div
      className="font-display text-4xl sm:text-5xl leading-none tabular-nums"
      style={{ color: "#fff", textShadow: `0 0 24px ${accent}55` }}
    >
      {pad ? String(value).padStart(2, "0") : value}
    </div>
    <div
      className="mt-2 text-[10px] font-mono-x tracking-[0.3em]"
      style={{ color: accent }}
    >
      {label}
    </div>
  </div>
);

const TikTokIcon = ({ size = 16 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M16.6 5.82a4.28 4.28 0 0 1-1.05-2.82h-3.1v12.36a2.6 2.6 0 1 1-2.6-2.6c.27 0 .53.04.78.12V9.7a5.7 5.7 0 0 0-.78-.05 5.78 5.78 0 1 0 5.78 5.78V9.18a7.36 7.36 0 0 0 4.3 1.38V7.5a4.32 4.32 0 0 1-3.33-1.68Z" />
  </svg>
);

export default Home;
